/**
 * usePostIntake — React wrapper around `postIntake`.
 *
 * Responsibilities:
 *   1. Read current session + state from IntakeContext
 *   2. For chat actions with a free-text message, append the user turn to
 *      history BEFORE posting (so the Worker sees the message)
 *   3. POST via the api client
 *   4. On success: merge state_delta into context; if the action is `chat`
 *      and the delta omits `history`, append the assistant reply as an
 *      assistant turn
 *   5. Surface { loading, error, data } so components can render states
 *
 * Components typically use this as:
 *   const { send, loading, error } = usePostIntake();
 *   await send({ action: 'chat', payload: { message: 'Hello' } });
 */

import { useCallback, useState } from 'react';
import { postIntake } from '../api/client';
import { NetworkError, WorkerHttpError, ContractError } from '../api/errors';
import type {
  Action,
  ChatPayload,
  HistoryTurn,
  IntakeResponse,
} from '../api/types';
import { useIntake } from '../state/IntakeContext';
import { logEvent } from '../lib/telemetry';

export interface SendArgs<P = unknown> {
  action: Action;
  payload: P;
  /** override timeout for this call — e.g. upload action needs longer */
  timeoutMs?: number;
}

export interface UsePostIntake {
  send: <P>(args: SendArgs<P>) => Promise<IntakeResponse>;
  loading: boolean;
  error: Error | null;
  lastResponse: IntakeResponse | null;
  clearError: () => void;
}

export function usePostIntake(): UsePostIntake {
  const { sessionId, state, mergeDelta, appendHistory } = useIntake();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastResponse, setLastResponse] = useState<IntakeResponse | null>(null);

  const send = useCallback(
    async <P,>({ action, payload, timeoutMs }: SendArgs<P>): Promise<IntakeResponse> => {
      setLoading(true);
      setError(null);

      // Pre-POST: for chat with a free-text message, capture the user turn
      // so the Worker's history input includes it.
      let workingState = state;
      const userTurn = extractUserTurn(action, payload);
      if (userTurn) {
        appendHistory(userTurn);
        workingState = { ...state, history: [...state.history, userTurn] };
      }

      try {
        const res = await postIntake({
          action,
          sessionId,
          state: workingState,
          payload,
          timeoutMs,
        });

        mergeDelta(res.state_delta);

        // Post-POST: any assistant reply that isn't part of a Worker-replaced
        // history goes into the local history as an assistant turn. Applies
        // to chat responses (normal), confirm responses (opening greeting),
        // and detect_language responses (if any). The Worker still owns the
        // authoritative summarized history via state_delta.history.
        if (
          res.reply !== null &&
          res.state_delta.history === undefined
        ) {
          appendHistory({ role: 'assistant', content: res.reply });
        }

        setLastResponse(res);
        return res;
      } catch (err) {
        const wrapped = err instanceof Error ? err : new Error(String(err));
        setError(wrapped);

        const attrs: Record<string, string | number | boolean> = {
          action,
          session_id: sessionId,
          error_type: wrapped.name,
          error_msg: wrapped.message.slice(0, 200),
        };
        if (err instanceof WorkerHttpError) attrs.http_status = err.status;
        if (err instanceof NetworkError)    attrs.timed_out = wrapped.message.includes('abort');
        if (err instanceof ContractError)   attrs.fatal = true;
        logEvent('api_error', attrs);

        throw wrapped;
      } finally {
        setLoading(false);
      }
    },
    [sessionId, state, mergeDelta, appendHistory],
  );

  const clearError = useCallback(() => setError(null), []);

  return { send, loading, error, lastResponse, clearError };
}

/** Pull a user HistoryTurn out of a chat payload when the user typed a
 *  free-text message. Returns null for constrained answers (no visible user
 *  text to add to history) and for non-chat actions. */
function extractUserTurn(action: Action, payload: unknown): HistoryTurn | null {
  if (action !== 'chat') return null;
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as ChatPayload;
  if ('message' in p && typeof p.message === 'string' && p.message.length > 0) {
    return { role: 'user', content: p.message };
  }
  return null;
}
